import { useEffect } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { fetchPublicTimeline } from "@/services/chat/publicTimelineApi";
import { mergePublicPhases } from "./publicTimelineState";

function interval(streaming) {
  return (query) =>
    streaming || query.state.data?.pages.at(-1)?.pending_count ? 1500 : false;
}

export function usePublicTimeline(
  runId,
  livePhases = [],
  streaming = false,
  persisted = false,
) {
  const query = useInfiniteQuery({
    queryKey: ["public-timeline", runId],
    enabled: Boolean(runId && (persisted || livePhases.length)),
    initialPageParam: null,
    queryFn: ({ pageParam, signal }) =>
      fetchPublicTimeline(runId, { cursor: pageParam, signal }),
    getNextPageParam: (page) => page.next_cursor ?? undefined,
    refetchInterval: interval(streaming),
  });
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = query;
  const pages = query.data?.pages ?? [];
  const phases = mergePublicPhases(
    livePhases,
    pages.flatMap((page) => page.phases),
  );
  const steps = pages.flatMap((page) => page.steps);
  const latest = pages.at(-1);
  const shouldFillPages = Boolean(streaming || latest?.pending_count);
  useEffect(() => {
    if (shouldFillPages && hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, shouldFillPages]);
  return {
    phases,
    steps,
    hasMore: hasNextPage,
    loadMore: fetchNextPage,
    loading: query.isLoading,
    startedAt: latest?.started_at ?? null,
    completedAt: latest?.completed_at ?? null,
  };
}
